import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DisplayCommunityAction } from '../../../../shared/models/displayCommunityAction';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { DisplayStructure } from '../../../../shared/models/displayStructure';
import { SupportTypes } from '../../../../shared/models/enums/support-type.enum';
import { labelsBySurveyContext, SurveyContext } from '../../../../shared/models/enums/survey-context-id.enum';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { BeneficiaryEmploymentService } from '../../../../shared/services/beneficiary-employment.service';
import { CommunityActionService } from '../../../../shared/services/community-action.service';
import { DateService } from '../../../../shared/services/date.service';
import { StructureService } from '../../../../shared/services/structure.service';
import { SurveysFSEService } from '../../../../shared/services/surveys-fse.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { Utils } from '../../../../shared/utils';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryEmploymentDto } from '../../../beneficiary-details/employment/dto/beneficiary-employment.dto';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { SurveyFSEDto } from '../surveyFSEDto';
import { SurveyFSESaveDto } from './surveyFseSave.dto';

// *********************************************************************************************
// * INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DAT *
// * INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DATA INITIAL-DAT *
// *********************************************************************************************

// Used to initialize some properties when opening modal from another component
export interface FseDetailInitialData {
  // Global
  year: string;
  setExitDate: boolean;
  structureId: number;
  beginDate: string;
  assessmentSourceId: number;
  labelId: number;

  // Questions
  entryQuestion7: '7Yes' | '7No';
  entryQuestion8: '8a' | '8b' | '8c' | '8d';
  entryQuestion1: '1aYes' | '1bYes' | '1cYes' | '1dYes' | '1gYes' | '1No';
  entryQuestion1e: '1eYes' | '1eNo';
  entryQuestion2: '2a' | '2b' | '2c' | '2d';
  entryQuestion9: '9Yes' | '9No';
  entryQuestion9a: Date;
  entryQuestion6: '6Yes' | '6No' | '6DK';
  entryQuestion4: '4Yes' | '4No';
  entryQuestion4c: '4cYes';
  entryQuestion5: '5Yes' | '5No' | '5DK';
  entryQuestion3: '3Yes' | 'No' | null;
}

// *********************************************************************************************
// * COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT *
// * COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT COMPONENT *
// *********************************************************************************************

@Component({
  selector: 'app-fse-details',
  templateUrl: './fse-details.component.html',
})
export class FseDetailsComponent implements OnInit, OnDestroy {
  protected submitted: boolean = false;

  public surveyFSE: SurveyFSEDto | undefined;
  private beneficiary: BeneficiaryDto;
  private employment: BeneficiaryEmploymentDto;
  public fseDetailsForm: UntypedFormGroup;
  private lastDayDate: Date;
  private accessMode: string;

  public entryFormQ1eIsActive: boolean = false;
  public entryFormQ1fIsActive: boolean = false;
  public entryFormQ1gIsActive: boolean = false;
  public entryFormQ9aIsActive: boolean = false;
  public exitFormQ1IsActive: boolean = false;
  public exitFormQ2IsActive: boolean = false;
  public isQuestion3Active: boolean = null;
  public entryFormQ4ValuesIsActive: boolean = false;

  public question1eValue1NoDisabled: boolean = null;

  public entryQuestion4Values: { letter: string; label: string }[] = [
    { letter: 'a', label: 'Revenu de solidarité active (RSA)' },
    { letter: 'b', label: 'Allocation spécifique de solidarité' },
    { letter: 'c', label: 'Allocation aux adultes handicapés' },
    { letter: 'd', label: 'Allocation de solidarité aux personnes âgées (minimum vieillesse)' },
    { letter: 'e', label: 'Revenu de solidarité' },
    { letter: 'f', label: 'Allocation veuvage' },
    { letter: 'g', label: 'Allocation supplémentaire d’invalidité' },
    { letter: 'h', label: 'Allocation pour demandeur d’asile' },
  ];

  public isCreation: boolean = false;

  public user: UserDto | undefined;
  private readonly destroy$: Subject<void> = new Subject();

  public structures: DisplayStructure[] = [];

  public years: string[] = [];
  public labels: DisplayLabel[] = [];

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly service: SurveysFSEService,
    private readonly communityActionService: CommunityActionService,
    private readonly dateService: DateService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly beneficiaryEmploymentService: BeneficiaryEmploymentService,
    private readonly structureService: StructureService,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    private changeDedectionRef: ChangeDetectorRef,
  ) {
    this.beneficiariesService.accessMode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((accessMode: string) => (this.accessMode = accessMode));

    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));
  }

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
  }

  // *********************************************************************************************
  // * INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT *
  // * INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT *
  // *********************************************************************************************

  public async onInit(surveyFseId?: number, data?: FseDetailInitialData): Promise<void> {
    await this.ngOnInit();
    this.lastDayDate = this.dateService.getCurrent();
    this.years = Utils.range(this.lastDayDate.getFullYear() - 2, this.lastDayDate.getFullYear() + 1)
      .reverse()
      .map((value: number) => value.toString());

    if (this.beneficiary.employmentId) {
      this.employment = await this.beneficiaryEmploymentService.findById(this.beneficiary.employmentId);
    } else {
      this.employment = undefined;
    }
    if (surveyFseId) {
      this.surveyFSE = await this.service.findBySurveyId(surveyFseId);

      this.structures = (await this.structureService.findAll()).filter(
        (structure: DisplayStructure) =>
          // Take all structures if not admin
          !this.user?.groups.includes('STRUCTURE') ||
          // Or take only user and potentially survey structure
          [this.user.structureId, this.surveyFSE?.structureId].includes(structure.id),
      );

      if (!this.years.includes(this.surveyFSE.year)) {
        this.years.push(this.surveyFSE.year);
      }
      this.labels = await this.generateLabels(this.surveyFSE.structureId, this.surveyFSE.year);

      const filteredAction: DisplayLabel = this.labels.find((item: DisplayLabel) => this.surveyFSE.communityActionId === item.id);

      if (!filteredAction) {
        const stageAction: DisplayCommunityAction = await this.communityActionService.findActionsLabelStageById(this.surveyFSE.communityActionId);
        if (stageAction) {
          this.labels = [...this.labels, { id: stageAction.id, label: stageAction.label }];
        }
      }

      const question3FalsyValues = ['3eExit', '3fExit', '3gExit'];
      const question3TruthyValues = ['3aExit', '3bExit', '3cExit', '3hExit', '3dExit'];
      const exitQuestion3Value = this.surveyFSE.exitQuestion3;
      this.isQuestion3Active = question3FalsyValues.includes(exitQuestion3Value)
        ? false
        : question3TruthyValues.includes(exitQuestion3Value)
          ? true
          : null;
      this.fseDetailsForm = this.formBuilder.group(
        {
          structure: [
            {
              value: this.surveyFSE.structureId,
              disabled: this.user?.groups.includes('STRUCTURE') && this.surveyFSE?.structureId !== this.user.structureId,
            },
            Validators.required,
          ],
          year: [this.surveyFSE.year, Validators.required],
          label: [this.surveyFSE.surveyContextId < 0 ? this.surveyFSE.surveyContextId : this.surveyFSE.communityActionId, Validators.required],
          assessmentSourceId: [this.surveyFSE.assessmentSourceId],

          entryForm: this.formBuilder.group({
            entryDate: [this.surveyFSE.beginDate, [DateValidators.dateFormatValidator]],
            entryQuestion1: [this.surveyFSE.entryQuestion1],
            entryQuestion1e: [this.surveyFSE.entryQuestion1e],
            entryQuestion1f: [this.surveyFSE.entryQuestion1f],
            searchingSince: [this.surveyFSE.searchingSince ?? null],
            entryQuestion2: [this.surveyFSE.entryQuestion2],
            entryQuestion3: [this.surveyFSE.entryQuestion3],
            entryQuestion4: [this.surveyFSE.entryQuestion4],
            entryQuestion4a: [this.surveyFSE.entryQuestion4a],
            entryQuestion4b: [this.surveyFSE.entryQuestion4b],
            entryQuestion4c: [this.surveyFSE.entryQuestion4c],
            entryQuestion4d: [this.surveyFSE.entryQuestion4d],
            entryQuestion4f: [this.surveyFSE.entryQuestion4f],
            entryQuestion4e: [this.surveyFSE.entryQuestion4e],
            entryQuestion4g: [this.surveyFSE.entryQuestion4g],
            entryQuestion4h: [this.surveyFSE.entryQuestion4h],
            entryQuestion5: [this.surveyFSE.entryQuestion5],
            entryQuestion6: [this.surveyFSE.entryQuestion6],
            entryQuestion7: [this.surveyFSE.entryQuestion7],
            entryQuestion8: [this.surveyFSE.entryQuestion8],
            entryQuestion9: [this.surveyFSE.entryQuestion9],
            entryQuestion9a: [this.surveyFSE.entryQuestion9a ?? null, DateValidators.dateFormatValidator],
          }),
          exitForm: this.formBuilder.group({
            exitDate: [this.surveyFSE.endDate ? this.surveyFSE.endDate : null, [DateValidators.dateFormatValidator]],
            exitQuestion1: [this.surveyFSE.exitQuestion1],
            exitQuestion2: [this.surveyFSE.exitQuestion2],
            exitLiminaire3: [this.surveyFSE.exitQuestion3 ? !['3eExit', '3fExit', '3gExit'].includes(this.surveyFSE.exitQuestion3) : null],
            exitQuestion3: [this.surveyFSE.exitQuestion3],
            exitQuestion4: [this.surveyFSE.exitQuestion4],
            exitQuestion5: [this.surveyFSE.exitQuestion5],
            exitQuestion6: [this.surveyFSE.exitQuestion6],
            exitQuestion7: [this.surveyFSE.exitQuestion7],
            exitQuestion8: [this.surveyFSE.exitQuestion8],
          }),
        },
        this.accessMode === 'retroactive'
          ? {
              validators: [
                DateValidators.datesOrderValidator('entryDate', 'exitDate', 'entryForm', 'exitForm'),
                DateValidators.sameYearValidator('entryDate', 'year'),
                this.entryOfExitValidator(),
              ],
            }
          : {
              validators: [
                DateValidators.datesOrderValidator('entryDate', 'exitDate', 'entryForm', 'exitForm'),
                DateValidators.datesOrderValidator('searchingSince', 'entryDate', 'entryForm', 'entryForm', false, 'searchingSinceDateOrder'),
                DateValidators.sameYearValidator('entryDate', 'year'),
                this.entryOfExitValidator(),
              ],
            },
      );

      if (this.user.groups.includes('STRUCTURE') && !['retroactive', 'participant'].includes(this.accessMode)) {
        this.entryForm.addValidators(DateValidators.datesOrderValidatorValue('entryDate', this.beneficiary.orientation.beginDate));
      }

      if (this.exitForm.controls.exitLiminaire3.value) {
        if (this.surveyFSE.exitQuestion3 === '3eExit' || this.surveyFSE.exitQuestion3 === '3fExit' || this.surveyFSE.exitQuestion3 === '3gExit') {
          this.isQuestion3Active = false;
        } else {
          this.isQuestion3Active = true;
        }
      }
      this.handleLabelValue();
      if (this.user?.groups.includes('TERRITORY')) {
        this.fseDetailsForm.disable();
      }
    } else {
      this.isCreation = true;
      this.lastDayDate.setMonth(11, 31);

      this.structures = (await this.structureService.findAll()).filter(
        (structure: DisplayStructure) =>
          // Take all structures if not admin
          !this.user?.groups.includes('STRUCTURE') ||
          // Or take only user and potentially survey structure
          [this.user.structureId].includes(structure.id),
      );
      this.fseDetailsForm = this.formBuilder.group(
        {
          structure: [null, Validators.required],
          year: [null, Validators.required],
          label: [null, Validators.required],
          assessmentSourceId: [null],
          entryForm: this.formBuilder.group({
            entryDate: [null, [DateValidators.dateFormatValidator]],
            entryQuestion1: [null],
            entryQuestion1e: [null],
            entryQuestion1f: [null],
            searchingSince: [null],
            entryQuestion2: [null],
            entryQuestion3: [null],
            entryQuestion4: [this.beneficiary.support.supportType.id === SupportTypes.ITE_ID_RENFORCE_RSA ? '4Yes' : null],
            entryQuestion4a: [this.beneficiary.support.supportType.id === SupportTypes.ITE_ID_RENFORCE_RSA ? '4aYes' : null],
            entryQuestion4b: [null],
            entryQuestion4c: [null],
            entryQuestion4d: [null],
            entryQuestion4e: [null],
            entryQuestion4f: [null],
            entryQuestion4g: [null],
            entryQuestion4h: [null],
            entryQuestion5: [null],
            entryQuestion6: [null],
            entryQuestion7: [this.beneficiary.birthCity !== 'Ville étrangère' ? (this.beneficiary.birthCity ? '7Yes' : null) : '7No'],
            entryQuestion8: [null],
            entryQuestion9: [this.beneficiary.lastUpdateDate ? '9Yes' : null],
            entryQuestion9a: [
              this.beneficiary.lastUpdateDate ? moment.utc(this.beneficiary.lastUpdateDate) : null,
              DateValidators.dateFormatValidator,
            ],
          }),
          exitForm: this.formBuilder.group({
            exitDate: [null, [DateValidators.dateFormatValidator]],
            exitQuestion1: [{ value: null, disabled: false }],
            exitQuestion2: [null],
            exitLiminaire3: [null],
            exitQuestion3: [null],
            exitQuestion4: [null],
            exitQuestion5: [null],
            exitQuestion6: [null],
            exitQuestion7: [null],
            exitQuestion8: [null],
          }),
        },
        this.accessMode === 'retroactive'
          ? {
              validators: [
                DateValidators.datesOrderValidator('entryDate', 'exitDate', 'entryForm', 'exitForm'),
                DateValidators.sameYearValidator('entryDate', 'year'),
                this.entryOfExitValidator(),
              ],
            }
          : {
              validators: [
                DateValidators.datesOrderValidator('entryDate', 'exitDate', 'entryForm', 'exitForm'),
                DateValidators.datesOrderValidator('searchingSince', 'entryDate', 'entryForm', 'entryForm', false, 'searchingSinceDateOrder'),
                DateValidators.sameYearValidator('entryDate', 'year'),
                this.entryOfExitValidator(),
              ],
            },
      );

      // Set entryQuestion2 depending of beneficiary degree
      switch (this.employment?.degreeId) {
        case 1:
          this.entryForm.controls.entryQuestion2.setValue('2a');
          break;
        case 2:
          this.entryForm.controls.entryQuestion2.setValue('2b');
          break;
        case 3:
          this.entryForm.controls.entryQuestion2.setValue('2c');
          break;
        case 4:
          this.entryForm.controls.entryQuestion2.setValue('2c');
          break;
        case 5:
          this.entryForm.controls.entryQuestion2.setValue('2d');
          break;
        case 6:
          this.entryForm.controls.entryQuestion2.setValue('2d');
          break;
        case 7:
          this.entryForm.controls.entryQuestion2.setValue('2d');
          break;
        case 8:
          this.entryForm.controls.entryQuestion2.setValue('2d');
          break;
      }

      if (this.user.groups.includes('STRUCTURE') && !['retroactive', 'participant'].includes(this.accessMode)) {
        this.entryForm.addValidators(DateValidators.datesOrderValidatorValue('entryDate', this.beneficiary.orientation.beginDate));
      }
      this.fseDetailsForm.controls.year.disable();
      this.fseDetailsForm.controls.label.disable();

      if (this.beneficiary.lastUpdateDate) {
        this.entryFormQ9aIsActive = true;
      }

      if (this.beneficiary.support.supportType.id === SupportTypes.ITE_ID_RENFORCE_RSA) {
        this.entryFormQ4ValuesIsActive = true;
      }
      this.handleLabelValue();
    }

    this.displayQuestion();

    this.entryForm.controls.entryDate?.valueChanges.subscribe((value) => this.handleEntryDateValue());
    this.handleEntryDateValue();
    this.exitForm.controls.exitDate?.valueChanges.subscribe((value) => this.handleExitDateValue());
    this.handleExitDateValue();

    this.fseDetailsForm.get('label').valueChanges.subscribe(() => this.handleLabelValue());

    this.exitForm.controls.exitLiminaire3.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => (this.isQuestion3Active = value));

    this.exitForm.controls.exitQuestion1.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => this.handleExitQuestion1Change(value, false));

    this.entryForm.controls.entryQuestion1.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => this.handleEntryQuestion1Change(value));

    this.entryForm.controls.entryQuestion1f.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => this.handleEntryQuestion1fChange(value));

    this.entryForm.controls.entryQuestion4.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => this.handleEntryQuestion4Change(value));

    this.entryForm.controls.entryQuestion9.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => this.handleEntryQuestion9Change(value));

    this.fseDetailsForm.controls.structure.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: number) => this.handleStructureChange(value));

    this.fseDetailsForm.controls.year.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => this.handleYearChange(value));
    this.setValidators();

    if (data) {
      await this.setInitialData(data);
    }
  }

  private async setInitialData(data: FseDetailInitialData): Promise<void> {
    // Global data
    if (data.labelId) {
      const searchLabels: DisplayLabel = this.labels.find((label: DisplayLabel) => label.id === data.labelId);
      if (!searchLabels) {
        if (!this.years.includes(data.year)) {
          this.years.push(data.year);
        }
        const stageAction: DisplayCommunityAction = await this.communityActionService.findActionsLabelStageById(data.labelId);
        if (stageAction) {
          this.labels = [{ id: stageAction.id, label: stageAction.label }];
        }
      }
    } else {
      this.labels = await this.generateLabels(data.structureId, data.year);
    }

    this.fseDetailsForm.controls.structure.setValue(data.structureId);
    this.fseDetailsForm.controls.year.setValue(data.year, { emitEvent: false });
    this.fseDetailsForm.controls.label.enable();
    this.fseDetailsForm.controls.label.setValue(data.labelId ?? null);
    this.fseDetailsForm.controls.assessmentSourceId.setValue(data.assessmentSourceId ?? null);

    // Entry form
    this.entryForm.controls.entryDate.setValue(data.beginDate);
    if (data.setExitDate) {
      this.lastDayDate.setFullYear(+data.year, 11, 31);
      this.exitForm.controls.exitDate.setValue(this.lastDayDate);
    }
    this.entryForm.controls.entryQuestion7.setValue(data.entryQuestion7 ?? null);
    this.entryForm.controls.entryQuestion8.setValue(data.entryQuestion8 ?? null);
    this.entryForm.controls.entryQuestion1.setValue(data.entryQuestion1 ?? null);
    this.entryForm.controls.entryQuestion1e.setValue(data.entryQuestion1e ?? null);
    this.entryForm.controls.entryQuestion9.setValue(data.entryQuestion9 ?? null);
    this.entryForm.controls.entryQuestion9a.setValue(data.entryQuestion9a ?? null);
    this.entryForm.controls.entryQuestion2.setValue(data.entryQuestion2 ?? null);
    this.entryForm.controls.entryQuestion6.setValue(data.entryQuestion6 ?? null);
    this.entryForm.controls.entryQuestion4.setValue(data.entryQuestion4 ?? null);
    this.entryForm.controls.entryQuestion4c.setValue(data.entryQuestion4c ?? null);
    this.entryForm.controls.entryQuestion5.setValue(data.entryQuestion5 ?? null);
    this.entryForm.controls.entryQuestion3.setValue(data.entryQuestion3 ?? null);
  }

  // *********************************************************************************************
  // * SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMI *
  // * SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMIT SUBMI *
  // *********************************************************************************************

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    this.addQuestionsValidators();

    if (!this.fseDetailsForm.valid || !this.entryForm.valid || !this.exitForm.valid) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données', 'danger');
      return;
    }
    try {
      await this.service.saveSurveFse(
        new SurveyFSESaveDto(
          this.surveyFSE?.id,
          this.surveyFSE?.version ?? 2, // If new item, then it is created with 2nd version of the FSE survey (since issue Insertis #58)
          this.beneficiary.id,
          this.fseDetailsForm.controls.label.value > 0 ? this.fseDetailsForm.controls.label.value : null,
          this.fseDetailsForm.controls.label.value <= 0 ? this.fseDetailsForm.controls.label.value : 0,
          this.surveyFSE?.assessmentSourceId || this.fseDetailsForm.controls.assessmentSourceId.value,
          this.fseDetailsForm.controls.structure.value,
          this.fseDetailsForm.controls.year.value,
          this.entryForm.controls.entryDate.value,
          this.exitForm.controls.exitDate.value,
          this.exitForm.controls.exitQuestion1.value,
          this.exitForm.controls.exitQuestion2.value,
          this.exitForm.controls.exitQuestion3.value,
          this.exitForm.controls.exitQuestion4.value,
          this.exitForm.controls.exitQuestion5.value,
          this.exitForm.controls.exitQuestion6.value,
          this.exitForm.controls.exitQuestion7.value,
          this.exitForm.controls.exitQuestion8.value,
          this.entryForm.controls.entryQuestion1.value,
          this.entryForm.controls.entryQuestion1e.value || '1eNo',
          this.entryForm.controls.entryQuestion1f.value,
          this.entryForm.controls.searchingSince.value ? this.entryForm.get('searchingSince').value : null,
          this.entryForm.controls.entryQuestion2.value,
          this.entryForm.controls.entryQuestion3.value,
          this.entryForm.controls.entryQuestion4.value,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4a.value ? '4aYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4b.value ? '4bYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4c.value ? '4cYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4d.value ? '4dYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4e.value ? '4eYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4f.value ? '4fYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4g.value ? '4gYes' : null,
          this.entryForm.controls.entryQuestion4.value === '4Yes' && this.entryForm.controls.entryQuestion4h.value ? '4hYes' : null,
          this.entryForm.controls.entryQuestion5.value,
          this.entryForm.controls.entryQuestion6.value,
          this.entryForm.controls.entryQuestion7.value,
          this.entryForm.controls.entryQuestion8.value,
          this.entryForm.controls.entryQuestion9.value,
          this.entryForm.controls.entryQuestion9a.value ? this.entryForm.get('entryQuestion9a').value : null,
        ),
        this.accessMode,
      );
      this.toastService.show('Le questionnaire a été sauvegardé avec succès', 'success');
      this.modal.close();
    } catch (error) {
      if (error.status === 442) {
        this.toastService.show("Impossible de créer, la date de création ne correspond pas à la période d'orientation passée", 'danger');
      }
    }
  }

  public structureCantSave(): boolean {
    if (this.isCreation === true) {
      return false;
    } else {
      return this.user?.groups.includes('STRUCTURE') && this.surveyFSE?.structureId !== this.user.structureId;
    }
  }
  // *********************************************************************************************
  // * DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELET *
  // * DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELETE DELET *
  // *********************************************************************************************

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.modal.close();
      await this.service.delete(this.surveyFSE.id);
      this.toastService.show('Le questionnaire a été supprimé', 'success');
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce questionnaire ?');
  }

  // *********************************************************************************************
  // * FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM *
  // * FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM *
  // *********************************************************************************************

  public addQuestionsValidators(): void {
    if (this.entryForm.controls.entryDate.value) {
      this.entryForm.get('entryQuestion1').addValidators(Validators.required);
      this.entryForm.get('entryQuestion2').addValidators(Validators.required);
      this.entryForm.get('entryQuestion3').addValidators(Validators.required);
      this.entryForm.get('entryQuestion4').addValidators(Validators.required);
      this.entryForm.get('entryQuestion5').addValidators(Validators.required);
      this.entryForm.get('entryQuestion6').addValidators(Validators.required);
      this.entryForm.get('entryQuestion7').addValidators(Validators.required);
      this.entryForm.get('entryQuestion8').addValidators(Validators.required);
      this.entryForm.get('entryQuestion9').addValidators(Validators.required);

      this.entryForm.get('entryQuestion1').updateValueAndValidity();
      this.entryForm.get('entryQuestion2').updateValueAndValidity();
      this.entryForm.get('entryQuestion3').updateValueAndValidity();
      this.entryForm.get('entryQuestion4').updateValueAndValidity();
      this.entryForm.get('entryQuestion5').updateValueAndValidity();
      this.entryForm.get('entryQuestion6').updateValueAndValidity();
      this.entryForm.get('entryQuestion7').updateValueAndValidity();
      this.entryForm.get('entryQuestion8').updateValueAndValidity();
      this.entryForm.get('entryQuestion9').updateValueAndValidity();
    }

    if (this.exitForm.controls.exitDate.value) {
      this.exitForm.get('exitQuestion1').addValidators(Validators.required);
      this.exitForm.get('exitQuestion3').addValidators(Validators.required);
      this.exitForm.get('exitQuestion4').addValidators(Validators.required);
      this.exitForm.get('exitQuestion5').addValidators(Validators.required);
      this.exitForm.get('exitQuestion6').addValidators(Validators.required);
      this.exitForm.get('exitQuestion7').addValidators(Validators.required);
      this.exitForm.get('exitQuestion8').addValidators(Validators.required);

      this.exitForm.get('exitQuestion3').updateValueAndValidity();
      this.exitForm.get('exitQuestion4').updateValueAndValidity();
      this.exitForm.get('exitQuestion5').updateValueAndValidity();
      this.exitForm.get('exitQuestion6').updateValueAndValidity();
      this.exitForm.get('exitQuestion7').updateValueAndValidity();
      this.exitForm.get('exitQuestion8').updateValueAndValidity();
    }
  }

  private setValidators(): void {
    if (this.surveyFSE?.exitQuestion1) {
      this.handleExitQuestion1Change(this.surveyFSE.exitQuestion1, true);
    }

    if (this.surveyFSE?.entryQuestion1 === '1No') {
      this.entryForm.get('entryQuestion1f').setValidators(Validators.required);
      if (this.surveyFSE.entryQuestion1f === '1fYes') {
        this.entryForm.get('searchingSince').setValidators([Validators.required, DateValidators.dateFormatValidator]);
      }
    } else {
      this.entryForm.get('entryQuestion1f').clearValidators();
      this.entryForm.get('searchingSince').clearValidators();
    }

    if (this.surveyFSE?.entryQuestion9 === '9Yes') {
      this.entryForm.get('entryQuestion9a').setValidators(Validators.required);
    } else {
      this.entryForm.get('entryQuestion9a').clearValidators();
    }

    this.entryForm.get('entryQuestion1e').updateValueAndValidity();
    this.entryForm.get('entryQuestion1f').updateValueAndValidity();
    this.entryForm.get('searchingSince').updateValueAndValidity();
    this.entryForm.get('entryQuestion9a').updateValueAndValidity();
  }

  public cancel(): void {
    this.modal.close();
  }

  private displayQuestion(): void {
    if (this.surveyFSE) {
      if (this.surveyFSE.entryQuestion1 === '1No') {
        this.entryFormQ1eIsActive = true;
      }

      if (this.surveyFSE.entryQuestion1f) {
        this.entryFormQ1fIsActive = true;
        if (this.surveyFSE.entryQuestion1f === '1fYes') {
          this.entryFormQ1gIsActive = true;
        }
      }

      if (this.surveyFSE.entryQuestion4 === '4Yes') {
        this.entryFormQ4ValuesIsActive = true;
      }

      if (this.surveyFSE.entryQuestion9 === '9Yes') {
        this.entryFormQ9aIsActive = true;
      }

      if (this.surveyFSE.exitQuestion1 === false) {
        this.exitFormQ1IsActive = true;
        this.exitFormQ2IsActive = true;
      }
      this.exitFormQ1IsActive = true;
    }
  }

  ngAfterContentChecked(): void {
    this.changeDedectionRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get entryForm(): UntypedFormGroup {
    return this.fseDetailsForm.controls.entryForm as UntypedFormGroup;
  }

  public get exitForm(): UntypedFormGroup {
    return this.fseDetailsForm.controls.exitForm as UntypedFormGroup;
  }

  public async handleStructureChange(id: number): Promise<void> {
    if (id === undefined) {
      this.fseDetailsForm.controls.year.reset();
      this.fseDetailsForm.controls.year.disable();
      this.fseDetailsForm.controls.label.reset();
      this.fseDetailsForm.controls.label.disable();
    } else {
      this.fseDetailsForm.controls.year.reset();
      this.fseDetailsForm.controls.year.enable();
      this.fseDetailsForm.controls.label.reset();
      this.fseDetailsForm.controls.label.disable();
    }
  }

  public async handleYearChange(year: string): Promise<void> {
    if (year === null) {
      this.fseDetailsForm.controls.label.disable();
    } else {
      this.labels = await this.generateLabels(this.fseDetailsForm.controls.structure.value, year);
      this.fseDetailsForm.controls.label.enable();
    }
  }

  private handleExitQuestion1Change(value: boolean, onInit: boolean): void {
    if (value) {
      this.exitFormQ2IsActive = false;
      this.exitForm.controls.exitQuestion2.setValue(null);
      this.exitForm.get('exitQuestion2').clearValidators();
    } else {
      this.exitFormQ2IsActive = true;
    }
    this.exitFormQ1IsActive = true;
  }

  private handleLabelValue(): void {
    const labelValue = this.fseDetailsForm.get('label').value;
    if (!this.exitForm.controls.exitDate.value) return;
    if (labelValue === SurveyContext.ANNUAL_MONITORING) {
      this.fseDetailsForm.get('exitForm.exitQuestion1').setValue(true);
      this.fseDetailsForm.get('exitForm.exitQuestion1').disable();
    } else {
      this.fseDetailsForm.get('exitForm.exitQuestion1').enable();
    }
  }

  private handleEntryDateValue(): void {
    this.setValidators();

    Object.entries<AbstractControl>(this.entryForm.controls).forEach(([key, control]) => {
      if (key == 'entryDate') return;
      if (!this.entryForm.controls.entryDate.value) {
        control.disable();
      } else {
        control.enable();
      }
    });
  }
  private handleExitDateValue(): void {
    this.setValidators();

    Object.entries<AbstractControl>(this.exitForm.controls).forEach(([key, control]) => {
      if (key == 'exitDate') return;
      if (!this.exitForm.controls.exitDate.value) {
        control.disable();
      } else {
        control.enable();
        // Manage exitQuestion1's disable
        this.handleLabelValue();
      }
    });
  }

  private handleEntryQuestion1Change(value: string): void {
    if (value === '1No') {
      this.entryFormQ1eIsActive = true;
      this.entryFormQ1fIsActive = true;
      this.entryForm.get('entryQuestion1e').setValidators(Validators.required);
      this.entryForm.get('entryQuestion1f').setValidators(Validators.required);
    } else {
      this.entryFormQ1eIsActive = false;
      this.entryFormQ1fIsActive = false;
      this.entryForm.controls.entryQuestion1e.setValue(null);
      this.entryForm.controls.entryQuestion1f.setValue(null);
      this.entryForm.get('entryQuestion1e').clearValidators();
      this.entryForm.get('entryQuestion1f').clearValidators();
      this.entryForm.get('searchingSince').clearValidators();
    }
    this.entryForm.get('entryQuestion1e').updateValueAndValidity();
    this.entryForm.get('entryQuestion1f').updateValueAndValidity();
    this.entryForm.get('searchingSince').updateValueAndValidity();
  }

  private handleEntryQuestion1fChange(value: string): void {
    if (value === '1fYes') {
      this.entryFormQ1gIsActive = true;
      this.entryForm.get('searchingSince').setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.question1eValue1NoDisabled = false;
    } else {
      this.entryFormQ1gIsActive = false;
      this.entryForm.controls.searchingSince.setValue(null);
      this.entryForm.get('searchingSince').clearValidators();
    }

    this.entryForm.get('searchingSince').updateValueAndValidity();
    this.entryForm.get('entryQuestion1e').updateValueAndValidity();
  }

  private handleEntryQuestion4Change(value: string): void {
    if (value === '4Yes') {
      this.entryFormQ4ValuesIsActive = true;
    } else {
      this.entryFormQ4ValuesIsActive = false;
      this.entryQuestion4Values.forEach((value) => this.entryForm.get('entryQuestion4' + value.letter).setValue(null));
    }
  }

  private handleEntryQuestion9Change(value: string): void {
    if (value === '9Yes') {
      this.entryFormQ9aIsActive = true;
      this.entryForm.get('entryQuestion9a').setValidators(Validators.required);
    } else {
      this.entryFormQ9aIsActive = false;
      this.entryForm.controls.entryQuestion9a.setValue(null);
      this.entryForm.get('entryQuestion9a').clearValidators();
    }
    this.entryForm.get('entryQuestion9a').updateValueAndValidity();
  }

  private async generateLabels(structureId: number, year: string): Promise<DisplayLabel[]> {
    const labels: DisplayLabel[] = await this.communityActionService.findActionsLabelFse(structureId, year);
    if (
      this.user?.groups.some((group: string) => ['ADMIN', 'DIRECTION+'].includes(group)) ||
      (this.user?.groups.includes('STRUCTURE') && this.beneficiary.orientation.structure.id === this.user?.structureId) ||
      ['participant', 'retroactive'].includes(this.accessMode)
    ) {
      Array.from(labelsBySurveyContext.entries()).forEach(([id, label]) => {
        if (id !== SurveyContext.COMMUNITY_ACTION) {
          labels.push(new DisplayLabel(id, label));
        }
      });
    }
    return labels;
  }

  private entryOfExitValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }
      if (!group.get('entryForm.entryDate')?.value && !group.get('exitForm.exitDate')?.value) return { entryOrExitDateRequired: true };
      return null;
    };
  }
}
